import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../api';
import AuthHeader from '../components/Header/AuthHeader';
import Button from '../components/Button';

interface FormData {
  accountType: 'bireysel' | 'isletme';
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  taxNumber: string;
  mersisNo: string;
  authorizedName: string;
  authorizedSurname: string;
  authorizedTcNo: string;
  companyEmail: string;
  phone: string;
  termsAccepted: boolean;
}

const Kayit: React.FC = () => {
  const { register, registerCorporate } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    accountType: 'bireysel',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    taxNumber: '',
    mersisNo: '',
    authorizedName: '',
    authorizedSurname: '',
    authorizedTcNo: '',
    companyEmail: '',
    phone: '',
    termsAccepted: false
  });
  const [error, setError] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  const handleGoogleSignUp = (): void => {
    window.location.href = authService.getGoogleLoginUrl();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.termsAccepted) {
      setError('Kullanım koşullarını kabul etmeniz gerekmektedir.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Şifre ve şifre tekrar alanları eşleşmiyor.');
      return;
    }

    setSubmitting(true);
    try {
      if (formData.accountType === 'bireysel') {
        const trimmedName = formData.name.trim();
        const spaceIndex = trimmedName.indexOf(' ');
        const firstName = spaceIndex > 0 ? trimmedName.slice(0, spaceIndex) : trimmedName;
        const lastName = spaceIndex > 0 ? trimmedName.slice(spaceIndex + 1) : '';
        await register({
          firstName: firstName || undefined,
          lastName: lastName || undefined,
          email: formData.email || undefined,
          password: formData.password || undefined,
        });
      } else {
        await registerCorporate({
          firstName: formData.authorizedName || undefined,
          lastName: formData.authorizedSurname || undefined,
          email: formData.companyEmail || undefined,
          password: formData.password || undefined,
          phoneNumber: formData.phone || undefined,
          companyName: formData.companyName || undefined,
          taxNumber: formData.taxNumber || undefined,
          nationalNumber: formData.authorizedTcNo || undefined,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kayıt başarısız. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AuthHeader />
      <div className="bg-light min-vh-100">
        <div className="container">
          <div className="row justify-content-center mt-5">
            <div className="col-md-6 col-lg-5">
              <div className="card shadow-sm">
                <div className="card-body p-4">
                  <h4 className="card-title text-center mb-4">
                    Kayıt Ol
                  </h4>

                {error && (
                  <div className="alert alert-danger mb-3" role="alert">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  {/* Hesap Türü Seçimi */}
                  <div className="mb-4">
                    <label className="form-label mb-3">
                      Hesap Türü
                    </label>
                    <div className="row g-3">
                      <div className="col-6">
                        <input
                          className="btn-check"
                          id="bireysel"
                          name="accountType"
                          type="radio"
                          value="bireysel"
                          checked={formData.accountType === 'bireysel'}
                          onChange={handleInputChange}
                        />
                        <label
                          className="btn btn-outline-primary w-100 d-flex flex-column align-items-center justify-content-center p-4"
                          htmlFor="bireysel"
                        >
                          <i className="fas fa-user fa-lg mb-2" />
                          <span className="fw-medium">
                            Bireysel
                          </span>
                          <small style={{ fontSize: '0.75rem', color: formData.accountType === 'bireysel' ? 'white' : '#0d6efd' }}>
                            Kişisel kullanım için
                          </small>
                        </label>
                      </div>
                      <div className="col-6">
                        <input
                          className="btn-check"
                          id="isletme"
                          name="accountType"
                          type="radio"
                          value="isletme"
                          checked={formData.accountType === 'isletme'}
                          onChange={handleInputChange}
                        />
                        <label
                          className="btn btn-outline-primary w-100 d-flex flex-column align-items-center justify-content-center p-4"
                          htmlFor="isletme"
                        >
                          <i className="fas fa-building fa-lg mb-2" />
                          <span className="fw-medium">
                            İşletme
                          </span>
                          <small style={{ fontSize: '0.75rem', color: formData.accountType === 'isletme' ? 'white' : '#0d6efd' }}>
                            Kurumsal kullanım için
                          </small>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Bireysel Form Alanları - required only when this section is visible */}
                  <div style={{ display: formData.accountType === 'bireysel' ? 'block' : 'none' }}>
                    <div className="mb-3">
                      <label className="form-label">
                        Ad Soyad
                      </label>
                      <input
                        className="form-control"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required={formData.accountType === 'bireysel'}
                        type="text"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        E-posta
                      </label>
                      <input
                        className="form-control"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required={formData.accountType === 'bireysel'}
                        type="email"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Şifre
                      </label>
                      <input
                        className="form-control"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required={formData.accountType === 'bireysel'}
                        type="password"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Şifre Tekrar
                      </label>
                      <input
                        className="form-control"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        required={formData.accountType === 'bireysel'}
                        type="password"
                      />
                    </div>
                  </div>

                  {/* İşletme Form Alanları - required only when this section is visible */}
                  <div style={{ display: formData.accountType === 'isletme' ? 'block' : 'none' }}>
                    <div className="mb-3">
                      <label className="form-label">
                        Şirket Adı
                      </label>
                      <input
                        className="form-control"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        placeholder="Şirket veya firma adı"
                        required={formData.accountType === 'isletme'}
                        type="text"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-medium">
                        Yetkili Kişi Bilgileri
                      </label>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <input
                            className="form-control"
                            name="authorizedName"
                            value={formData.authorizedName}
                            onChange={handleInputChange}
                            placeholder="Ad"
                            required={formData.accountType === 'isletme'}
                            type="text"
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <input
                            className="form-control"
                            name="authorizedSurname"
                            value={formData.authorizedSurname}
                            onChange={handleInputChange}
                            placeholder="Soyad"
                            required={formData.accountType === 'isletme'}
                            type="text"
                          />
                        </div>
                      </div>
                      <input
                        className="form-control mb-3"
                        name="authorizedTcNo"
                        value={formData.authorizedTcNo}
                        onChange={handleInputChange}
                        placeholder="T.C. Kimlik No"
                        required={formData.accountType === 'isletme'}
                        type="text"
                      />
                      <input
                        className="form-control"
                        name="taxNumber"
                        value={formData.taxNumber}
                        onChange={handleInputChange}
                        placeholder="Vergi Numarası"
                        required={formData.accountType === 'isletme'}
                        type="text"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        İşletme E-posta
                      </label>
                      <input
                        className="form-control"
                        name="companyEmail"
                        value={formData.companyEmail}
                        onChange={handleInputChange}
                        required={formData.accountType === 'isletme'}
                        type="email"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Telefon
                      </label>
                      <input
                        className="form-control"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        type="tel"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Şifre
                      </label>
                      <input
                        className="form-control"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required={formData.accountType === 'isletme'}
                        type="password"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Şifre Tekrar
                      </label>
                      <input
                        className="form-control"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        required={formData.accountType === 'isletme'}
                        type="password"
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        name="termsAccepted"
                        checked={formData.termsAccepted}
                        onChange={handleInputChange}
                        required
                        type="checkbox"
                      />
                      <label className="form-check-label">
                        <Link to="/kullanici-sozlesmesi" style={{marginRight: '5px'}}>
                          Kullanım koşullarını
                        </Link>
                        kabul ediyorum
                      </label>
                    </div>
                  </div>

                  <Button
                    className="btn btn-primary w-100 mb-3"
                    type="submit"
                    disabled={submitting}
                    loading={submitting}
                  >
                    Kayıt Ol
                  </Button>

                  {formData.accountType === 'bireysel' && (
                    <>
                      <div className="text-center mb-3">
                        <span className="text-muted">
                          veya
                        </span>
                      </div>
                      <Button
                        className="btn btn-outline-dark w-100 mb-3"
                        type="button"
                        onClick={handleGoogleSignUp}
                      >
                        <i className="fab fa-google me-2" style={{ fontSize: '1.1rem' }} aria-hidden />
                        Google ile Kayıt Ol
                      </Button>
                    </>
                  )}

                  <div className="text-center">
                    <span className="text-muted">
                      Zaten hesabınız var mı?
                    </span>
                    <Link className="text-decoration-none ms-1" to="/giris">
                      Giriş Yap
                    </Link>
                  </div>
                  <div className="text-center mt-2">
                    <Link className="text-decoration-none small" to="/sifremi-unuttum">
                      Şifremi Unuttum
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Kayit;
